/**
 * KIAN Assistant Chat Hook
 *
 * Zustand store for managing assistant chat state and API calls.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { createAssistantApi } from '../lib/api/domains/assistant';
import type { AssistantPageContextPayload } from '../lib/api/domains/assistant';
import { apiRequest } from '../lib/api/http';
import type { ChatSession, ChatSessionListItem, ChatMessage } from '../lib/api/domains/assistant';
import { postAssistantStream } from '../lib/api/assistantStream';
import { busyReplyKey, isReplyBusy, mergePendingReply, type PendingReply } from './assistantPendingReplies';

const assistantApi = createAssistantApi(apiRequest);

// AbortController je Gespräch mit laufendem Stream. Nicht im Zustand gespeichert (nicht
// serialisierbar, würde alle Subscriber triggern) — dort steht nur `pendingReplies`.
const streamControllers = new Map<string, AbortController>();
// Verzögertes Ausblenden der Upload-Progress-Bar, je Gespräch — ein neuer Upload bricht es ab.
const progressHideTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

export type { ChatSession, ChatMessage };
export type { PendingReply };

export const MAX_OPEN_SESSIONS = 5;

export interface AssistantChatState {
  // State
  currentSessionId: string | null;
  /** Liste bis zu 5 gleichzeitig geöffneter Sessions (Tabs) */
  openSessionIds: string[];
  /** Minimale Kopfdaten (Titel, Entity) für Tabs ohne Voll-Load */
  openSessionMeta: Record<string, { title: string; entityLabel?: string | null; entityType?: string | null }>;
  sessions: ChatSessionListItem[];
  currentSession: ChatSession | null;
  isLoading: boolean;
  /**
   * Laufende Antworten je Gespräch (`isReplyBusy`). Mehrere Gespräche dürfen gleichzeitig auf
   * KIAN warten; „KIAN antwortet", Stopp-Knopf, Upload-Fortschritt und gesperrte Eingabe gelten
   * nur für das Gespräch, dem die Antwort gehört.
   */
  pendingReplies: Record<string, PendingReply>;
  error: string | null;
  totalSessions: number;
  sessionsLoaded: boolean;

  // Actions
  createSession: (opts?: {
    submissionId?: string;
    title?: string;
    entityType?: string;
    entityId?: string;
    entityLabel?: string;
    forceNew?: boolean;
  }) => Promise<string>;
  /** Tab in Leiste öffnen; lädt + aktiviert. Wirft, wenn Limit erreicht. */
  openSessionTab: (sessionId: string) => Promise<void>;
  /** Tab schließen. Entfernt aus Liste; aktiviert Nachbar. */
  closeSessionTab: (sessionId: string) => void;
  loadSession: (sessionId: string) => Promise<void>;
  loadSessions: (opts?: {
    limit?: number;
    offset?: number;
    status?: string;
    entityType?: string;
    entityId?: string;
  }) => Promise<void>;
  sendMessage: (
    content: string,
    files?: File[],
    pageContext?: AssistantPageContextPayload,
    streamOpts?: { useStream?: boolean; signal?: AbortSignal; hints?: Record<string, unknown> }
  ) => Promise<void>;
  /** Stream eines Gesprächs abbrechen (ohne ID: das angezeigte). Nur im useStream-Pfad verfügbar. */
  abortStream: (sessionId?: string) => void;
  /** Eine Assistant-Antwort erneut generieren (basierend auf vorheriger User-Message). */
  regenerateMessage: (
    messageId: string,
    pageContext?: AssistantPageContextPayload,
    streamReplies?: boolean,
    hints?: Record<string, unknown>
  ) => Promise<void>;
  archiveSession: (sessionId: string) => Promise<void>;
  unarchiveSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  setCurrentSession: (sessionId: string | null) => void;
  /** Flyout: Anzeige leeren, wenn Kontext nicht zur geladenen Session passt (verhindert falsche Messages). */
  clearActiveSession: () => void;
  clearError: () => void;
  /** Vom Server gespeicherte Nachrichten anhängen (Gesprächsmodus: „Ja, anlegen", Ergebnis …) */
  appendMessages: (sessionId: string, messages: ChatMessage[]) => void;
  /** Nur Admins: Werkzeug-Budget dieses Gesprächs setzen (`null` = globale Einstellung) */
  setSessionToolBudget: (sessionId: string, perMessage: number | null) => Promise<void>;
}

/**
 * Frisch geladenen Stand übernehmen und eine laufende Antwort darin einsetzen — der Server kennt
 * sie erst, wenn sie fertig ist. Kennt er sie schon, fällt der Eintrag weg.
 */
function withPendingReply(
  state: AssistantChatState,
  session: ChatSession,
): Pick<AssistantChatState, 'currentSession'> & Partial<Pick<AssistantChatState, 'pendingReplies'>> {
  const { messages, settled } = mergePendingReply(session.messages, state.pendingReplies[session.id]);
  if (!settled) return { currentSession: messages === session.messages ? session : { ...session, messages } };
  const { [session.id]: _settled, ...rest } = state.pendingReplies;
  return { currentSession: session, pendingReplies: rest };
}

// ============================================
// Store
// ============================================

export const useAssistantChat = create<AssistantChatState>((set, get) => ({
  currentSessionId: null,
  openSessionIds: [],
  openSessionMeta: {},
  sessions: [],
  currentSession: null,
  isLoading: false,
  pendingReplies: {},
  error: null,
  totalSessions: 0,
  sessionsLoaded: false,

  createSession: async (opts) => {
    set({ isLoading: true, error: null });
    try {
      const response = await assistantApi.createSession({
        submissionId: opts?.submissionId,
        title: opts?.title,
        entityType: opts?.entityType ?? undefined,
        entityId: opts?.entityId ?? undefined,
        entityLabel: opts?.entityLabel ?? undefined,
        forceNew: opts?.forceNew ?? false,
      });
      const sessionId = response.sessionId;
      set((state) => {
        const alreadyOpen = state.openSessionIds.includes(sessionId);
        const nextOpen = alreadyOpen
          ? state.openSessionIds
          : state.openSessionIds.length < MAX_OPEN_SESSIONS
            ? [...state.openSessionIds, sessionId]
            : state.openSessionIds;
        const meta = state.openSessionMeta[sessionId] ?? {
          title: opts?.title || opts?.entityLabel || 'Freies Gespräch',
          entityLabel: opts?.entityLabel ?? null,
          entityType: opts?.entityType ?? null,
        };
        return {
          currentSessionId: sessionId,
          openSessionIds: nextOpen,
          openSessionMeta: { ...state.openSessionMeta, [sessionId]: meta },
          isLoading: false,
        };
      });
      return sessionId;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create session';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  openSessionTab: async (sessionId: string) => {
    const state = get();
    if (!state.openSessionIds.includes(sessionId)) {
      if (state.openSessionIds.length >= MAX_OPEN_SESSIONS) {
        const msg = `Maximal ${MAX_OPEN_SESSIONS} Chats gleichzeitig offen.`;
        set({ error: msg });
        throw new Error(msg);
      }
      set((s) => ({ openSessionIds: [...s.openSessionIds, sessionId] }));
    }
    try {
      const session = await assistantApi.getSession(sessionId);
      set((s) => ({
        ...withPendingReply(s, session),
        currentSessionId: sessionId,
        openSessionMeta: {
          ...s.openSessionMeta,
          [sessionId]: {
            title: session.title,
            entityLabel: session.entityLabel ?? null,
            entityType: session.entityType ?? null,
          },
        },
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load session';
      set({ error: message });
      throw error;
    }
  },

  closeSessionTab: (sessionId: string) => {
    set((state) => {
      const nextOpen = state.openSessionIds.filter((id) => id !== sessionId);
      const { [sessionId]: _removed, ...remainingMeta } = state.openSessionMeta;
      let nextCurrent = state.currentSessionId;
      let nextCurrentSession = state.currentSession;
      if (state.currentSessionId === sessionId) {
        nextCurrent = nextOpen[nextOpen.length - 1] ?? null;
        nextCurrentSession = null;
      }
      return {
        openSessionIds: nextOpen,
        openSessionMeta: remainingMeta,
        currentSessionId: nextCurrent,
        currentSession: nextCurrentSession,
      };
    });
    const { currentSessionId } = get();
    if (currentSessionId) {
      void get().loadSession(currentSessionId);
    }
  },

  loadSession: async (sessionId: string) => {
    set({ isLoading: true, error: null });
    try {
      const session = await assistantApi.getSession(sessionId);
      set((state) => {
        const nextOpen = state.openSessionIds.includes(sessionId)
          ? state.openSessionIds
          : state.openSessionIds.length < MAX_OPEN_SESSIONS
            ? [...state.openSessionIds, sessionId]
            : state.openSessionIds;
        return {
          ...withPendingReply(state, session),
          currentSessionId: sessionId,
          openSessionIds: nextOpen,
          openSessionMeta: {
            ...state.openSessionMeta,
            [sessionId]: {
              title: session.title,
              entityLabel: session.entityLabel ?? null,
              entityType: session.entityType ?? null,
            },
          },
          isLoading: false,
        };
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load session';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  loadSessions: async (opts) => {
    const limit = opts?.limit ?? 20;
    const offset = opts?.offset ?? 0;
    const status = opts?.status ?? 'ACTIVE';
    const entityType = opts?.entityType;
    const entityId = opts?.entityId;
    set({ isLoading: true, error: null });
    try {
      const response = await assistantApi.getSessions({
        limit,
        offset,
        status,
        ...(entityType ? { entityType } : {}),
        ...(entityId ? { entityId } : {}),
      });

      set({
        sessions: response.sessions,
        totalSessions: response.total,
        sessionsLoaded: true,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load sessions';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  sendMessage: async (content, files, pageContext, streamOpts) => {
    const hints = streamOpts?.hints;
    const { currentSessionId, currentSession } = get();
    if (!currentSessionId) {
      set({ error: 'No session selected' });
      return;
    }

    if (!currentSession) {
      set({ error: 'Session data not loaded' });
      return;
    }

    // Die Antwort gehört dem Gespräch, das gerade zu sehen ist — auch wenn später gewechselt wird
    const sessionId = currentSession.id;
    // Je Gespräch eine Antwort zur Zeit (die Eingabe ist solange gesperrt)
    if (isReplyBusy(get().pendingReplies[sessionId])) return;

    const optimisticId = `user-${Date.now()}`;
    const optimisticAttachments = (files ?? []).map((file, index) => ({
      id: `tmp-${optimisticId}-${index}`,
      filename: file.name,
      mimeType: file.type,
      size: file.size,
      url: URL.createObjectURL(file),
    }));
    const revokeOptimistic = () => {
      optimisticAttachments.forEach((a) => {
        try {
          URL.revokeObjectURL(a.url);
        } catch {
          /* ignore */
        }
      });
    };

    const userMessage: ChatMessage = {
      id: optimisticId,
      role: 'USER',
      content,
      createdAt: new Date().toISOString(),
      ...(optimisticAttachments.length > 0 ? { attachments: optimisticAttachments } : {}),
      // Gesprächsmodus: gesprochen, nicht getippt (Symbol in der Blase)
      ...(hints?.voice === true ? { metadata: { voice: true } } : {}),
    };

    /** Anzeige ändern — nur, wenn dieses Gespräch gerade zu sehen ist */
    const inView = (
      cs: ChatSession | null,
      fn: (messages: ChatMessage[]) => ChatMessage[],
    ): { currentSession?: ChatSession } =>
      cs && cs.id === sessionId ? { currentSession: { ...cs, messages: fn(cs.messages), updatedAt: new Date().toISOString() } } : {};
    // Nur die eigene Antwort ändern: Ein Stream liest nach „done" noch bis zum Verbindungsende,
    // und in der Zeit kann im selben Gespräch schon die nächste Frage laufen.
    const ownReply = (s: AssistantChatState) => {
      const entry = s.pendingReplies[sessionId];
      return entry?.token === optimisticId ? entry : undefined;
    };
    const patchReply = (s: AssistantChatState, patch: Partial<PendingReply>) => {
      const entry = ownReply(s);
      return entry ? { pendingReplies: { ...s.pendingReplies, [sessionId]: { ...entry, ...patch } } } : {};
    };
    const dropReply = (s: AssistantChatState) => {
      if (!ownReply(s)) return {};
      const { [sessionId]: _dropped, ...rest } = s.pendingReplies;
      return { pendingReplies: rest };
    };
    /** Fehler in einem Gespräch im Hintergrund: sagen, welches — sonst stünde er am falschen */
    const errorFor = (s: AssistantChatState, msg: string) => {
      if (s.currentSession?.id === sessionId) return msg;
      const title = s.openSessionMeta[sessionId]?.title ?? s.sessions.find((x) => x.id === sessionId)?.title;
      return title ? `„${title}": ${msg}` : msg;
    };

    // Immer streamen — außer bei Datei-Uploads (Multipart-Endpunkt hat keinen Stream)
    const useStream = !files || files.length === 0;

    if (useStream) {
      const assistantId = `assistant-stream-${Date.now()}`;
      const assistantMessage: ChatMessage = {
        id: assistantId,
        role: 'ASSISTANT',
        content: '',
        createdAt: new Date().toISOString(),
        metadata: { streaming: true },
      };

      set((s) => ({
        ...inView(s.currentSession, (msgs) => [...msgs, userMessage, assistantMessage]),
        pendingReplies: {
          ...s.pendingReplies,
          [sessionId]: {
            token: optimisticId,
            kind: 'stream',
            userMessage,
            assistantMessage,
            startedAt: Date.now(),
            done: false,
            isAnalyzingFiles: false,
            uploadProgress: null,
          },
        },
        error: null,
      }));

      // AbortController für diesen Stream vorbereiten
      const controller = new AbortController();
      streamControllers.set(sessionId, controller);
      const externalSignal = streamOpts?.signal;
      if (externalSignal) {
        // Pre-check: signal may already be aborted before we attach
        if (externalSignal.aborted) {
          controller.abort();
        } else {
          externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
        }
      }

      /** Abgebrochen: Teil-Antwort stehen lassen */
      const markAborted = () =>
        set((s) => ({
          ...inView(s.currentSession, (msgs) =>
            msgs.map((m) =>
              m.id === assistantId
                ? { ...m, content: acc || '_(Antwort abgebrochen)_', metadata: { ...m.metadata, streaming: false, aborted: true } }
                : m
            )
          ),
          ...dropReply(s),
        }));

      let acc = '';
      try {
        await postAssistantStream(
          sessionId,
          content,
          pageContext,
          hints,
          {
            onDelta: (text) => {
              acc += text;
              const withText = (m: ChatMessage) => (m.id === assistantId ? { ...m, content: acc } : m);
              set((s) => {
                const entry = ownReply(s);
                return {
                  ...inView(s.currentSession, (msgs) => msgs.map(withText)),
                  ...(entry?.assistantMessage ? patchReply(s, { assistantMessage: withText(entry.assistantMessage) }) : {}),
                };
              });
            },
            onDone: (meta) => {
              const finished: ChatMessage = {
                ...assistantMessage,
                id: meta.messageId,
                content: acc,
                metadata: {
                  ...assistantMessage.metadata,
                  durationMs: meta.durationMs,
                  model: meta.model,
                  inputTokens: meta.inputTokens,
                  outputTokens: meta.outputTokens,
                  costEur: meta.costEur,
                  tier: meta.tier,
                  ...(meta.actionIds?.length ? { actionIds: meta.actionIds } : {}),
                  ...(meta.voice ? { voice: true } : {}),
                  ...(meta.voiceDraft ? { voiceDraft: meta.voiceDraft } : {}),
                  ...(meta.toolBudgetExhausted ? { toolBudgetExhausted: true, toolBudget: meta.toolBudget } : {}),
                  streaming: false,
                },
              };
              set((s) => ({
                ...inView(s.currentSession, (msgs) => msgs.map((m) => (m.id === assistantId ? finished : m))),
                ...patchReply(s, { done: true, assistantMessage: finished }),
              }));
            },
            onError: (msg) => {
              set((s) => ({
                ...inView(s.currentSession, (msgs) => msgs.filter((m) => m.id !== assistantId)),
                ...dropReply(s),
                error: errorFor(s, msg),
              }));
            },
          },
          controller.signal
        );
        // Stream zu Ende ohne „done" oder „error": abgebrochen (Stopp mitten im Lesen) oder
        // Verbindung still beendet — sonst stünde das Gespräch für immer auf „KIAN antwortet".
        if (isReplyBusy(ownReply(get()))) {
          if (controller.signal.aborted) {
            markAborted();
          } else {
            set((s) => ({
              ...inView(s.currentSession, (msgs) =>
                msgs.map((m) => (m.id === assistantId ? { ...m, metadata: { ...m.metadata, streaming: false } } : m))
              ),
              ...dropReply(s),
              ...(acc ? {} : { error: errorFor(s, 'Keine Antwort erhalten') }),
            }));
          }
        }
      } catch (error) {
        // AbortError (User hat gestoppt): aktuelle Teil-Antwort stehen lassen.
        if (error instanceof Error && error.name === 'AbortError') {
          markAborted();
        } else {
          const message = error instanceof Error ? error.message : 'Stream fehlgeschlagen';
          set((s) => ({
            ...inView(s.currentSession, (msgs) =>
              acc
                ? msgs.map((m) => (m.id === assistantId ? { ...m, metadata: { ...m.metadata, streaming: false } } : m))
                : msgs.filter((m) => m.id !== assistantId)
            ),
            ...dropReply(s),
            error: errorFor(s, message),
          }));
        }
      } finally {
        if (streamControllers.get(sessionId) === controller) {
          streamControllers.delete(sessionId);
        }
      }
      return;
    }

    // Optimistisch direkt anzeigen (inkl. Attachment-Previews per Object-URL),
    // damit der User während der Vision-Analyse sein Bild sieht.
    const hasFiles = !!(files && files.length > 0);
    const hideTimeout = progressHideTimeouts.get(sessionId);
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      progressHideTimeouts.delete(sessionId);
    }
    set((s) => ({
      ...inView(s.currentSession, (msgs) => [...msgs, userMessage]),
      pendingReplies: {
        ...s.pendingReplies,
        [sessionId]: {
          token: optimisticId,
          kind: 'upload',
          userMessage,
          assistantMessage: null,
          startedAt: Date.now(),
          done: false,
          isAnalyzingFiles: hasFiles,
          uploadProgress: hasFiles ? 0 : null,
        },
      },
      error: null,
    }));

    // Progress-Simulation: Stufenweise auf 90% hochzählen während der Request läuft.
    // Kein echter fetch-Progress verfügbar ohne XHR, aber Fake-Progress gibt Feedback.
    let progressInterval: ReturnType<typeof setInterval> | null = null;
    if (hasFiles) {
      let simulated = 0;
      progressInterval = setInterval(() => {
        simulated = Math.min(90, simulated + 5 + Math.random() * 10);
        set((s) => patchReply(s, { uploadProgress: Math.round(simulated) }));
      }, 250);
    }

    try {
      const response = await assistantApi.sendMessage(
        sessionId,
        { content, pageContext, hints },
        files
      );
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }

      const persistedUserMessage: ChatMessage = response.userMessage
        ? {
            id: response.userMessage.id,
            role: 'USER',
            content: response.userMessage.content,
            createdAt: response.userMessage.createdAt,
            ...(response.userMessage.attachments.length > 0
              ? { attachments: response.userMessage.attachments }
              : {}),
          }
        : userMessage;

      const assistantMessage: ChatMessage = {
        id: response.messageId,
        role: 'ASSISTANT',
        content: response.assistantMessage,
        createdAt: new Date().toISOString(),
        metadata: response.metadata,
      };

      set((s) => ({
        ...inView(s.currentSession, (msgs) => [
          ...msgs.filter((m) => m.id !== optimisticId && m.id !== persistedUserMessage.id && m.id !== assistantMessage.id),
          persistedUserMessage,
          assistantMessage,
        ]),
        ...patchReply(s, {
          done: true,
          userMessage: persistedUserMessage,
          assistantMessage,
          isAnalyzingFiles: false,
          uploadProgress: hasFiles ? 100 : null,
        }),
      }));
      // Progress-Bar kurz bei 100% stehen lassen, dann verstecken.
      if (hasFiles) {
        progressHideTimeouts.set(
          sessionId,
          setTimeout(() => {
            progressHideTimeouts.delete(sessionId);
            set((s) => patchReply(s, { uploadProgress: null }));
          }, 400)
        );
      }
      revokeOptimistic();
    } catch (error) {
      if (progressInterval) {
        clearInterval(progressInterval);
        progressInterval = null;
      }
      revokeOptimistic();
      const message = error instanceof Error ? error.message : 'Failed to send message';
      set((s) => ({
        ...inView(s.currentSession, (msgs) => msgs.filter((m) => m.id !== optimisticId)),
        ...dropReply(s),
        error: errorFor(s, message),
      }));
      throw error;
    }
  },

  abortStream: (sessionId) => {
    const id = sessionId ?? get().currentSession?.id ?? null;
    if (!id) return;
    const controller = streamControllers.get(id);
    if (controller) {
      controller.abort();
      streamControllers.delete(id);
    }
  },

  regenerateMessage: async (messageId, pageContext, streamReplies, hints) => {
    const cs = get().currentSession;
    if (!cs) return;
    // Solange hier noch eine Antwort läuft, schickte sendMessage nichts — die Nachrichten wären weg
    if (isReplyBusy(get().pendingReplies[cs.id])) return;

    const msgIndex = cs.messages.findIndex((m) => m.id === messageId);
    if (msgIndex < 1) return;

    // Die Message davor muss eine User-Message sein
    const prevMsg = cs.messages[msgIndex - 1];
    if (prevMsg.role !== 'USER') return;

    const userContent = prevMsg.content;

    // Sowohl die User-Message als auch die Assistant-Message entfernen,
    // damit sendMessage beide neu erstellt (kein Duplikat).
    const trimmedMessages = cs.messages.filter(
      (m) => m.id !== messageId && m.id !== prevMsg.id
    );
    set({
      currentSession: {
        ...cs,
        messages: trimmedMessages,
        updatedAt: new Date().toISOString(),
      },
    });

    // Erneut senden. Files können nicht wiederhergestellt werden (als URLs im
    // DOM, nicht mehr als File-Objekt verfügbar) — der Content enthält aber
    // bereits die Vision-Analyse aus der ersten Runde, das reicht für Regen.
    await get().sendMessage(userContent, undefined, pageContext, {
      useStream: streamReplies === true,
      hints,
    });
  },

  archiveSession: async (sessionId: string) => {
    set({ isLoading: true, error: null });
    try {
      await assistantApi.archiveSession(sessionId);

      // Remove from current sessions list
      set((state) => {
        const nextOpen = state.openSessionIds.filter((id) => id !== sessionId);
        const { [sessionId]: _removed, ...remainingMeta } = state.openSessionMeta;
        return {
          sessions: state.sessions.filter((s) => s.id !== sessionId),
          openSessionIds: nextOpen,
          openSessionMeta: remainingMeta,
          currentSessionId: state.currentSessionId === sessionId ? (nextOpen[nextOpen.length - 1] ?? null) : state.currentSessionId,
          currentSession: state.currentSessionId === sessionId ? null : state.currentSession,
          isLoading: false,
        };
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to archive session';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  unarchiveSession: async (sessionId: string) => {
    set({ isLoading: true, error: null });
    try {
      await assistantApi.unarchiveSession(sessionId);
      set((state) => ({
        sessions: state.sessions.filter((s) => s.id !== sessionId),
        isLoading: false,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to unarchive session';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteSession: async (sessionId: string) => {
    set({ isLoading: true, error: null });
    try {
      await assistantApi.deleteSession(sessionId);
      // Eine Antwort für ein gelöschtes Gespräch hat kein Ziel mehr
      get().abortStream(sessionId);

      // Remove from current sessions list
      set((state) => {
        const nextOpen = state.openSessionIds.filter((id) => id !== sessionId);
        const { [sessionId]: _removed, ...remainingMeta } = state.openSessionMeta;
        const { [sessionId]: _reply, ...remainingReplies } = state.pendingReplies;
        return {
          pendingReplies: remainingReplies,
          sessions: state.sessions.filter((s) => s.id !== sessionId),
          openSessionIds: nextOpen,
          openSessionMeta: remainingMeta,
          currentSessionId: state.currentSessionId === sessionId ? (nextOpen[nextOpen.length - 1] ?? null) : state.currentSessionId,
          currentSession: state.currentSessionId === sessionId ? null : state.currentSession,
          isLoading: false,
        };
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete session';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  setCurrentSession: (sessionId: string | null) => {
    set({ currentSessionId: sessionId });
  },

  clearActiveSession: () => {
    set({ currentSessionId: null, currentSession: null });
  },

  clearError: () => {
    set({ error: null });
  },

  appendMessages: (sessionId: string, messages: ChatMessage[]) => {
    const cs = get().currentSession;
    if (!cs || cs.id !== sessionId || messages.length === 0) return;
    const known = new Set(cs.messages.map((m) => m.id));
    const fresh = messages.filter((m) => !known.has(m.id));
    if (!fresh.length) return;
    set({ currentSession: { ...cs, messages: [...cs.messages, ...fresh], updatedAt: new Date().toISOString() } });
  },

  setSessionToolBudget: async (sessionId, perMessage) => {
    const { toolBudget } = await assistantApi.setToolBudget(sessionId, perMessage);
    set((s) =>
      s.currentSession?.id === sessionId ? { currentSession: { ...s.currentSession, toolBudget } } : {},
    );
  },
}));

/** Gespräche, in denen KIAN gerade antwortet — ändert sich nur, wenn eins dazukommt oder fertig wird */
export function useBusyReplySessionIds(): ReadonlySet<string> {
  const key = useAssistantChat((s) => busyReplyKey(s.pendingReplies));
  return useMemo(() => new Set(key ? key.split(',') : []), [key]);
}
